import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DateRange } from 'react-day-picker';
import { buildPresetRange } from '@/utils/period-presets';
import { Layout } from '@/components/layout/Layout';
import { VideoDuelHeader } from '@/components/video-duel/VideoDuelHeader';
import { VideoSelectorBlock } from '@/components/video-duel/VideoSelectorBlock';
import { ComparisonDataTable } from '@/components/video-duel/ComparisonDataTable';
import { VideoDuelEmptyState } from '@/components/video-duel/VideoDuelEmptyState';
import { VideoDuelSkeleton } from '@/components/video-duel/VideoDuelSkeleton';
import { VideoDuelErrorState } from '@/components/video-duel/VideoDuelErrorState';
import type { ComparedVideo, VideoDuelComparisonResponse } from '@/components/video-duel/videoDuelData';
import { videoService } from '@/services/videoService';
import { videoDuelService } from '@/services/videoDuelService';

export default function VideoDuel() {
  useLanguage();
  const [videoA, setVideoA] = useState<string | null>(null);
  const [videoB, setVideoB] = useState<string | null>(null);
  const [videoC, setVideoC] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  const [availableVideos, setAvailableVideos] = useState<ComparedVideo[]>([]);
  const [videosLoading, setVideosLoading] = useState(false);

  const [comparison, setComparison] = useState<VideoDuelComparisonResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  // Posição do pitch na tabela consolidada; fica aqui para não voltar ao 1º a cada refetch.
  const [pitchSelection, setPitchSelection] = useState<string | null>(null);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
    buildPresetRange('last30Days', new Date())
  );

  // Carrega lista de vídeos do usuário ao iniciar a comparação
  useEffect(() => {
    if (!started || availableVideos.length > 0) return;
    setVideosLoading(true);
    videoService
      .getVideosList({ limit: 200, includeFolderVideos: true })
      .then((res) => {
        const mapped: ComparedVideo[] = (res.videos || []).map((v: any) => ({
          id: v.id,
          name: v.title,
          thumbnailUrl: v.thumbnailUrl || '',
          durationInSeconds: Math.round(v.duration || 0),
          folderName: v.folderName || undefined,
        }));
        setAvailableVideos(mapped);
      })
      .catch((err) => {
        console.error('[VideoDuel] erro ao carregar vídeos:', err);
        setError(translateInterfaceText("Não foi possível carregar a lista de vídeos"));
      })
      .finally(() => setVideosLoading(false));
  }, [started, availableVideos.length]);

  const hasComparison = !!videoA && !!videoB;
  const showEmpty = !started && !videoA && !videoB && !videoC;

  // Dispara comparação quando há A + B (e opcionalmente C)
  useEffect(() => {
    if (!hasComparison || !dateRange?.from || !dateRange?.to) return;
    setIsLoading(true);
    setError(null);
    setComparison(null);

    const ids = [videoA, videoB, videoC].filter(Boolean) as string[];
    videoDuelService
      .compare({
        videoIds: ids,
        inicio: dateRange.from.toISOString(),
        fim: dateRange.to.toISOString(),
      })
      .then((data) => {
        setComparison(data);
      })
      .catch((err) => {
        console.error('[VideoDuel] erro na comparação:', err);
        setError(err?.response?.data?.error?.message || translateInterfaceText("Não foi possível carregar a comparação"));
      })
      .finally(() => setIsLoading(false));
  }, [videoA, videoB, videoC, dateRange?.from, dateRange?.to, loadKey, hasComparison]);

  const handleRetry = useCallback(() => setLoadKey((k) => k + 1), []);

  const selectedVideos = useMemo(() => {
    if (!comparison) return [];
    return comparison.videos;
  }, [comparison]);

  return (
    <Layout>
      <div className="w-full px-4 sm:px-6 py-6 space-y-8">
        <VideoDuelHeader />

        {showEmpty ? (
          <VideoDuelEmptyState onStart={() => setStarted(true)} />
        ) : (
          <>
            <VideoSelectorBlock
              videoA={videoA}
              videoB={videoB}
              videoC={videoC}
              onSelectA={setVideoA}
              onSelectB={setVideoB}
              onSelectC={setVideoC}
              videos={availableVideos}
              retention={comparison?.retention || null}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />

            {hasComparison && isLoading && <VideoDuelSkeleton />}

            {hasComparison && !isLoading && error && (
              <VideoDuelErrorState message={error} onRetry={handleRetry} />
            )}

            {hasComparison && !isLoading && !error && comparison && (
              <div className="space-y-6">
                <ComparisonDataTable
                  metrics={comparison.metrics}
                  videos={selectedVideos}
                  retention={comparison.retention}
                  pitchSelection={pitchSelection}
                  onPitchSelectionChange={setPitchSelection}
                  onPitchSaved={handleRetry}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
